/**
 * How a grouped stock page nests its lines — chosen by the user, kept for the user.
 *
 * The same four words everywhere a stock page is grouped (and the customer pages
 * later): company, location, point of sale, depot, plus the article where the page can
 * group by it. The user picks which levels to use and in what order — « point de vente
 * puis dépôt », « dépôt seul », « article puis dépôt » — and the choice follows them
 * across devices, one arrangement per page.
 *
 * The saved value is only a list of words. Whatever the server hands back is checked
 * against what the page can group by, so an old or hand-edited value falls back to the
 * page's default instead of breaking it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ReferentialApi, StockGroupLevel } from "../api";
import { NAMESPACE } from "../i18n";

export const ALL_LEVELS: readonly StockGroupLevel[] = [
  "company",
  "city",
  "point",
  "site",
  "article",
];

/** The saved words that are usable on this page: known, distinct and not empty — or null. */
export function validLevels(
  saved: unknown,
  allowed: readonly StockGroupLevel[],
): StockGroupLevel[] | null {
  if (!Array.isArray(saved) || saved.length === 0) return null;
  const picked: StockGroupLevel[] = [];
  for (const entry of saved) {
    const level = allowed.find((candidate) => candidate === entry);
    if (!level || picked.includes(level)) return null;
    picked.push(level);
  }
  return picked;
}

export interface Grouping {
  levels: readonly StockGroupLevel[];
  /** False until the saved arrangement was asked for: the page waits, so it never loads twice. */
  ready: boolean;
  change: (next: readonly StockGroupLevel[]) => void;
  reset: () => void;
  isDefault: boolean;
}

/** The user's arrangement of one page: read once, written whenever it changes. */
export function useGrouping(
  api: ReferentialApi,
  key: string,
  defaults: readonly StockGroupLevel[],
  allowed: readonly StockGroupLevel[],
): Grouping {
  const [levels, setLevels] = useState<readonly StockGroupLevel[]>(defaults);
  const [ready, setReady] = useState(false);
  // Not saved back until the user has changed it: reading must never write.
  const touched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .layouts()
      .then((saved) => {
        if (cancelled || touched.current) return;
        const usable = validLevels(saved[key], allowed);
        if (usable) setLevels(usable);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
    // `allowed` is a constant of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key]);

  const change = useCallback(
    (next: readonly StockGroupLevel[]) => {
      if (next.length === 0) return;
      touched.current = true;
      setLevels(next);
      // A failed save leaves the choice on screen; the next change tries again.
      void api.saveLayout(key, next).catch(() => undefined);
    },
    [api, key],
  );
  const reset = useCallback(() => change(defaults), [change, defaults]);

  const isDefault = useMemo(
    () => levels.length === defaults.length && levels.every((level, i) => level === defaults[i]),
    [levels, defaults],
  );
  return { levels, ready, change, reset, isDefault };
}

/** The wording of one level, as a column header or a choice. */
export function useLevelName() {
  const { t } = useTranslation(NAMESPACE);
  return (level: StockGroupLevel) => t(`grouping.level.${level}`);
}
